import type { SupabaseClient } from '@supabase/supabase-js';
import type { CategoriaGasto, MetodoPago } from '../gastos/types';
import { assertMonedaPermitidaParaEmpresa, type EmpresaMonedaContext } from '../../utils/empresaMoneda';
import {
  mapCierreDiario,
  type AggregateLine,
  type BalanceByMethod,
  type CierreDiarioRow,
} from './types';

function num(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === 'number') return Math.round(v);
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : 0;
}

export async function fetchCierreDiario(
  supabase: SupabaseClient,
  empresaId: string,
  fecha: string
): Promise<CierreDiarioRow | null> {
  const { data, error } = await supabase
    .from('cierres_diarios')
    .select('*')
    .eq('empresa_id', empresaId)
    .eq('fecha', fecha)
    .maybeSingle();
  if (error) throw error;
  return data ? mapCierreDiario(data as Record<string, unknown>) : null;
}

export async function getOrCreateCierreDiario(
  supabase: SupabaseClient,
  empresaId: string,
  fecha: string,
  usuarioId?: string | null
): Promise<CierreDiarioRow> {
  const existing = await fetchCierreDiario(supabase, empresaId, fecha);
  if (existing) return existing;

  const insert: Record<string, unknown> = {
    empresa_id: empresaId,
    fecha,
  };
  if (usuarioId) insert.created_by = usuarioId;

  const { data, error } = await supabase
    .from('cierres_diarios')
    .upsert(insert, { onConflict: 'empresa_id,fecha' })
    .select('*')
    .single();

  if (!error && data) {
    return mapCierreDiario(data as Record<string, unknown>);
  }

  if (error && (error as { code?: string }).code === '23505') {
    const again = await fetchCierreDiario(supabase, empresaId, fecha);
    if (again) return again;
  }
  if (error) throw error;
  throw new Error('No se pudo crear el cierre del día');
}

export async function getIngresosDia(
  supabase: SupabaseClient,
  empresaId: string,
  fecha: string
): Promise<AggregateLine[]> {
  const { data, error } = await supabase.rpc('cierre_get_ingresos_dia', {
    p_empresa_id: empresaId,
    p_fecha: fecha,
  });
  if (error) throw error;
  return (data || []) as AggregateLine[];
}

export async function getGastosDia(
  supabase: SupabaseClient,
  empresaId: string,
  fecha: string
): Promise<AggregateLine[]> {
  const { data, error } = await supabase.rpc('cierre_get_gastos_dia', {
    p_empresa_id: empresaId,
    p_fecha: fecha,
  });
  if (error) throw error;
  return (data || []) as AggregateLine[];
}

export async function getIngresosPeriodo(
  supabase: SupabaseClient,
  empresaId: string,
  desde: string,
  hasta: string
): Promise<AggregateLine[]> {
  const { data, error } = await supabase.rpc('cierre_get_ingresos_periodo', {
    p_empresa_id: empresaId,
    p_desde: desde,
    p_hasta: hasta,
  });
  if (error) throw error;
  return (data || []) as AggregateLine[];
}

export async function getGastosPeriodo(
  supabase: SupabaseClient,
  empresaId: string,
  desde: string,
  hasta: string
): Promise<AggregateLine[]> {
  const { data, error } = await supabase.rpc('cierre_get_gastos_periodo', {
    p_empresa_id: empresaId,
    p_desde: desde,
    p_hasta: hasta,
  });
  if (error) throw error;
  return (data || []) as AggregateLine[];
}

export async function getTopGastosCategoria(
  supabase: SupabaseClient,
  empresaId: string,
  desde: string,
  hasta: string,
  limit = 10
): Promise<{ categoria: string; total: number }[]> {
  const { data, error } = await supabase.rpc('cierre_top_gastos_categoria_periodo', {
    p_empresa_id: empresaId,
    p_desde: desde,
    p_hasta: hasta,
    p_limit: limit,
  });
  if (error) throw error;
  return ((data || []) as { categoria: string; total: number | string }[]).map((r) => ({
    categoria: r.categoria,
    total: num(r.total),
  }));
}

export async function getTopLineasFactura(
  supabase: SupabaseClient,
  empresaId: string,
  desde: string,
  hasta: string,
  limit = 15
): Promise<{ nombre: string; total: number }[]> {
  const { data, error } = await supabase.rpc('cierre_top_lineas_factura_periodo', {
    p_empresa_id: empresaId,
    p_desde: desde,
    p_hasta: hasta,
    p_limit: limit,
  });
  if (error) throw error;
  return ((data || []) as { nombre: string; total: number | string }[]).map((r) => ({
    nombre: r.nombre,
    total: num(r.total),
  }));
}

export async function lockCierreDiario(
  supabase: SupabaseClient,
  cierreId: string,
  saldoFinal: BalanceByMethod,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from('cierres_diarios')
    .update({
      status: 'cerrado',
      locked_by: userId,
      locked_at: new Date().toISOString(),
      saldo_final_efectivo: saldoFinal.efectivo,
      saldo_final_banco: saldoFinal.banco,
      saldo_final_nequi: saldoFinal.nequi,
      saldo_final_daviplata: saldoFinal.daviplata,
      saldo_final_tarjeta: saldoFinal.tarjeta,
      saldo_final_otro: saldoFinal.otro,
    })
    .eq('id', cierreId);
  if (error) throw error;
}

export async function unlockCierreDiario(
  supabase: SupabaseClient,
  cierreId: string,
  empresaId: string,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from('cierres_diarios')
    .update({
      status: 'abierto',
      locked_by: null,
      locked_at: null,
      saldo_final_efectivo: null,
      saldo_final_banco: null,
      saldo_final_nequi: null,
      saldo_final_daviplata: null,
      saldo_final_tarjeta: null,
      saldo_final_otro: null,
    })
    .eq('id', cierreId);
  if (error) throw error;

  await supabase.from('cierres_audit_log').insert({
    empresa_id: empresaId,
    action: 'unlock_cierre_diario',
    entity: 'cierres_diarios',
    entity_id: cierreId,
    done_by: userId,
    meta: {},
  });
}

export async function updateSaldosIniciales(
  supabase: SupabaseClient,
  cierreId: string,
  inicial: BalanceByMethod,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from('cierres_diarios')
    .update({
      saldo_inicial_efectivo: inicial.efectivo,
      saldo_inicial_banco: inicial.banco,
      saldo_inicial_nequi: inicial.nequi,
      saldo_inicial_daviplata: inicial.daviplata,
      saldo_inicial_tarjeta: inicial.tarjeta,
      saldo_inicial_otro: inicial.otro,
      override_inicial: true,
      override_by: userId,
    })
    .eq('id', cierreId);
  if (error) throw error;
}

export async function insertGastoRapido(
  supabase: SupabaseClient,
  params: {
    empresaId: string;
    usuarioId?: string | null;
    fecha: string;
    descripcion: string;
    monto: number;
    metodoPago: MetodoPago;
    categoria: CategoriaGasto;
    empresaCtx?: EmpresaMonedaContext;
    moneda?: string | null;
  }
): Promise<void> {
  assertMonedaPermitidaParaEmpresa(params.empresaCtx, params.moneda);
  const { error } = await supabase.from('gastos').insert({
    empresa_id: params.empresaId,
    usuario_id: params.usuarioId || null,
    descripcion: params.descripcion.trim(),
    categoria: params.categoria,
    monto: Math.round(params.monto),
    fecha: params.fecha,
    metodo_pago: params.metodoPago,
    estado: 'pagado',
    fecha_pago: params.fecha,
    activo: true,
    deducible: false,
    recurrente: false,
  });
  if (error) throw error;
}

export function balanceToRowPatch(s: BalanceByMethod): Record<string, number> {
  return {
    saldo_final_efectivo: s.efectivo,
    saldo_final_banco: s.banco,
    saldo_final_nequi: s.nequi,
    saldo_final_daviplata: s.daviplata,
    saldo_final_tarjeta: s.tarjeta,
    saldo_final_otro: s.otro,
  };
}
